import React, { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { UserContext } from "../../context/UserContext";
import { db } from "../../firebase/firebaseConfig";
import { Trophy, RefreshCw } from "lucide-react";

const ResultScreen = () => {
  const { state } = useLocation();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { score = 0, total = 1, profileId, difficulty } = state || {};

  const accuracy = ((score / total) * 100).toFixed(2);

  useEffect(() => {
    if (user) {
      const userRef = doc(db, "users", user.id);
      updateDoc(userRef, {
        interviewResults: arrayUnion({
          profile: profileId,
          difficulty,
          score,
          total,
          accuracy,
          date: new Date().toISOString(),
        }),
      });
    }
  }, [user, score, total, profileId, difficulty, accuracy]);

  return (
    <Wrapper>
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Header>
          <Trophy size={40} color="#c59fff" />
          <Title>Interview Results</Title>
        </Header>
      </motion.div>

      <Card
        as={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        <ScoreCircle>
          <CircleOuter>
            <CircleFill accuracy={accuracy} />
            <CircleText>{accuracy}%</CircleText>
          </CircleOuter>
        </ScoreCircle>

        <ScoreDetails>
          <Profile>{profileId?.toUpperCase()}</Profile>
          <DifficultyTag>{difficulty}</DifficultyTag>
          <ScoreValue>
            {score} / {total}
          </ScoreValue>
          <AccuracyText>Accuracy: {accuracy}%</AccuracyText>
        </ScoreDetails>

        <ButtonGroup>
          <GradientButton onClick={() => navigate(`/interview/${profileId}`)}>
            <RefreshCw size={18} />
            &nbsp; Retake Test
          </GradientButton>
          <OutlineButton onClick={() => navigate("/interview")}>
            Back to Prep
          </OutlineButton>
        </ButtonGroup>
      </Card>
    </Wrapper>
  );
};

export default ResultScreen;

// ---------------- Styled Components ----------------

const Wrapper = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2rem;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 22px;
  padding: 40px;
  text-align: center;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 30px rgba(155, 92, 255, 0.2);
  backdrop-filter: blur(10px);
`;

const ScoreCircle = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;

const CircleOuter = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  position: relative;
  background: radial-gradient(circle at top left, #1a1129, #0a0816);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CircleFill = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(
    #9b5cff ${({ accuracy }) => accuracy}%,
    rgba(255, 255, 255, 0.08) ${({ accuracy }) => accuracy}%
  );
  transform: rotate(-90deg);
`;

const CircleText = styled.span`
  position: relative;
  font-size: 1.6rem;
  font-weight: 700;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ScoreDetails = styled.div`
  margin-bottom: 25px;
`;

const Profile = styled.h3`
  font-size: 1.3rem;
  color: #c59fff;
  margin-bottom: 8px;
`;

const DifficultyTag = styled.span`
  background: rgba(155, 92, 255, 0.15);
  color: #c59fff;
  border: 1px solid rgba(155, 92, 255, 0.4);
  padding: 4px 12px;
  border-radius: 10px;
  font-size: 0.9rem;
`;

const ScoreValue = styled.h2`
  margin-top: 15px;
  font-size: 1.4rem;
  font-weight: 600;
`;

const AccuracyText = styled.p`
  font-size: 0.95rem;
  color: #d3caff;
  margin-top: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
  flex-wrap: wrap;
`;

const GradientButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px rgba(155, 92, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.5);
  }
`;

const OutlineButton = styled.button`
  background: transparent;
  border: 1px solid rgba(155, 92, 255, 0.6);
  color: #c59fff;
  border-radius: 12px;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: rgba(155, 92, 255, 0.15);
    transform: translateY(-2px);
  }
`;
